import { Link } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePWA } from '@/hooks/usePWA';
import { APP_VERSION } from '@/config/version';
import { LogoIcon } from '@/components/LogoIcon';
import { AppStoreBadges } from '@/components/AppStoreBadges';

const productLinks = [
  { href: '/features', label: 'Features' },
  { href: '/how-to-use', label: 'How to Use' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/schools', label: 'For Schools' },
  { href: '/download', label: 'Download' },
];

const companyLinks = [
  { href: '/about', label: 'About' },
  { href: '/compare', label: 'Why Leka?' },
  { href: '/privacy', label: 'Privacy Policy' },
  { href: '/terms', label: 'Terms of Service' },
];

export function Footer() {
  const { isInstalled } = usePWA();
  const { user } = useAuth();

  // Hide in PWA mode entirely; hide on mobile for authenticated users (bottom nav handles it)
  if (isInstalled) return null;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className={`container mx-auto px-4 py-12 mt-auto border-t border-border ${user ? 'hidden md:block' : ''}`}>
      {/* Main grid */}
      <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr] gap-8 mb-8">
        {/* Brand column */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <LogoIcon size="sm" />
            <span className="font-display text-lg font-bold text-foreground">Leka</span>
          </div>
          <p className="text-sm text-muted-foreground max-w-[260px]">
            Save words you discover. Remember them forever.
          </p>
          <AppStoreBadges height="h-10" className="justify-start gap-3" playStoreScaleClass="scale-[1.25]" />
        </div>

        {/* Product column */}
        <nav aria-label="Product links">
          <h3 className="font-display text-sm font-semibold text-foreground mb-3">Product</h3>
          <ul className="flex flex-col gap-2">
            {productLinks.map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors focus-ring"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Company column */}
        <nav aria-label="Company links">
          <h3 className="font-display text-sm font-semibold text-foreground mb-3">Company</h3>
          <ul className="flex flex-col gap-2">
            {companyLinks.map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors focus-ring"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between pt-6 border-t border-border text-sm text-muted-foreground">
        <p className="flex items-center gap-2">
          <span>© {new Date().getFullYear()} Leka</span>
          <span className="text-muted-foreground">v{APP_VERSION}</span>
        </p>
        <button
          onClick={scrollToTop}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus-ring"
          aria-label="Back to top"
        >
          <ArrowUp className="w-4 h-4" />
          <span className="hidden sm:inline">Back to top</span>
        </button>
      </div>
    </footer>
  );
}
